import { createSlice } from "@reduxjs/toolkit";
import { refreshAPI, loginAPI, logoutAPI } from "properties/config.json";
import { COMMON_PROPERTIES } from "properties/client.js";
import jwt from "jsonwebtoken";
import moment from "moment";
import { http, notifier } from "common";

export const loginSlice = createSlice({
  name: "login",
  initialState: {
    jwtClaim: null,
    refreshTimer: null,
  },
  reducers: {
    setJwtClaim: (state, action) => {
      let claim = { ...action.payload };
      if (claim && claim.token) {
        try {
          let decoded = jwt.decode(action.payload.token, { complete: true });
          claim.tokenDetail = { ...decoded };
          localStorage.setItem("CHAKRA_IC", JSON.stringify(claim));
        } catch (error) {
          console.log(error);
        }
      }
      state.jwtClaim = { ...claim };
    },
    setRefreshTimer: (state, action) => {
      if (state.refreshTimer) {
        clearTimeout(state.refreshTimer);
      }
      state.refreshTimer = action.payload.refreshTimer;
    },
    setApiAuthToken: (state, action) => {
      let token = action.payload ? action.payload.token : "";
      http.setAuthToken(token);
    },
  },
});

export const {
  setJwtClaim,
  setRefreshTimer,
  setApiAuthToken,
} = loginSlice.actions;

export const setJwtClaimAsync = (data, props) => (dispatch) => {
  http.getIPAddress().then((ip) => {
    http
      .post(loginAPI, { ...data, userip: ip })
      .then((response) => {
        dispatch(setJwtClaim({ ...response.data }));
        props.history.replace("/");
        notifier.success(COMMON_PROPERTIES.LOG_IN_SUCCESS_MSG);
      })
      .catch((error) => {
        dispatch(setJwtClaim(null));
        notifier.error(COMMON_PROPERTIES.LOG_IN_FAILED_MSG);
      });
  });
};

export const setJwtClaimRefreshAsync = (username, refreshtoken) => (
  dispatch
) => {
  localStorage.removeItem("CHAKRA_IC");
  http
    .post(refreshAPI, { username, refreshtoken })
    .then(({ status, data }) => {
      if (status === 200) {
        dispatch(setJwtClaim({ ...data }));
        localStorage.setItem("CHAKRA_IC", JSON.stringify(data));
        notifier.success(COMMON_PROPERTIES.LOG_IN_REFRESH_SUCCESS_MSG);
      } else {
        notifier.error(COMMON_PROPERTIES.LOG_IN_REFRESH_FAILURE_MSG);
      }
    })
    .catch((error) => {
      dispatch(setJwtClaim(null));
      // notifier.success(COMMON_PROPERTIES.LOG_IN_REFRESH_FAILURE_MSG);
    });
};

export const setJwtClaimEmptyAsync = (data, props) => (dispatch) => {
  try {
    http
      .post(logoutAPI, { username: data.username })
      .then((response) => {
        localStorage.removeItem("CHAKRA_IC");
        const currLoginLocationPathname = localStorage.getItem("iccrLoginLocationPathname");
        props.history.push(currLoginLocationPathname !== undefined ? currLoginLocationPathname : "/login");
        dispatch(setJwtClaim({ ...response.data }));
        notifier.success(COMMON_PROPERTIES.LOG_OUT_SUCCESS_MSG);
      })
      .catch((error) => {
        console.log(error);
        dispatch(setJwtClaim(null));
        notifier.success(COMMON_PROPERTIES.LOG_OUT_FAILURE_MSG);
      });
  } catch (e) {
    console.log(e);
  }
};

export const setRefreshTimerAsync = (jwtClaim) => (dispatch) => {
  let timer = null;
  try {
    let { username, refreshtoken, tokenDetail } = jwtClaim;
    if (tokenDetail && tokenDetail.payload) {
      let interval = moment(tokenDetail.payload.exp * 1000)
        .subtract(2, "m")
        .diff(moment());
      if (username && refreshtoken && interval) {
        timer = setTimeout(() => {
          dispatch(setJwtClaimRefreshAsync(username, refreshtoken));
        }, interval);
        moment().add(interval, "ms").fromNow();
      }
    }
  } catch (error) {
    console.log("setRefreshTimerAsync catch: ", error);
  } finally {
    dispatch(setRefreshTimer({ refreshTimer: timer }));
  }
};

export const selectJwtClaim = (state) => {
  return state.login.jwtClaim;
};

export const selectJwtClaimUsername = (state) => {
  if (state.login.jwtClaim) {
    return state.login.jwtClaim.username;
  } else {
    return null;
  }
};

export const selectJwtClaimSubject = (state) => {
  if (state.login.jwtClaim && state.login.jwtClaim.tokenDetail) {
    return state.login.jwtClaim.tokenDetail.payload.sub;
  } else {
    return null;
  }
};

export const selectJwtClaimAuthorization = (state) => {
  if (state.login.jwtClaim && state.login.jwtClaim.tokenDetail) {
    return state.login.jwtClaim.tokenDetail.payload.aut;
  } else {
    return null;
  }
};

export const selectJwtClaimUserRoles = (state) => {
  if (state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.roles) {
    return state.login.jwtClaim.additionalInfo.roles;
  } else {
    return [];
  }
};

export const selectJwtClaimUserZones = (state) => {
  if (state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.zones) {
    return state.login.jwtClaim.additionalInfo.zones;
  } else {
    return [];
  }
};

export const selectJwtClaimUserLevels = (state) => {
  if (state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.levels) {
    return state.login.jwtClaim.additionalInfo.levels;
  } else {
    return [];
  }
};

export const selectJwtClaimUserLevel = (state) => {
  if (
    state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.levels
    && state.login.jwtClaim.additionalInfo.levels.length > 0
  ) {
    return state.login.jwtClaim.additionalInfo.levels[0].userLevel;
  } else {
    return "Unknown";
  }
};

export const selectJwtClaimUserType = (state) => {
  if (
    state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.levels
    && state.login.jwtClaim.additionalInfo.levels.length > 0
  ) {
    return state.login.jwtClaim.additionalInfo.levels[0].userType;
  } else {
    return "Unknown";
  }
};

export const selectJwtClaimUserInstitutes = (state) => {
  if (state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.institutes) {
    return state.login.jwtClaim.additionalInfo.institutes;
  } else {
    return [];
  }
};

export const selectJwtClaimUserCountries = (state) => {
  if (state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.countries) {
    return state.login.jwtClaim.additionalInfo.countries;
  } else {
    return [];
  }
};

export const selectJwtClaimUserEntities = (state) => {
  if (state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.entities) {
    return state.login.jwtClaim.additionalInfo.entities;
  } else {
    return [];
  }
};

export const selectJwtClaimUserAssocMissionEntities = (state) => {
  if (state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.assocMissionEntities) {
    return state.login.jwtClaim.additionalInfo.assocMissionEntities;
  } else {
    return [];
  }
};

export const selectJwtClaimUserAssocICCEntities = (state) => {
  if (state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.assocICCEntities) {
    return state.login.jwtClaim.additionalInfo.assocICCEntities;
  } else {
    return [];
  }
};

export const selectJwtClaimUserEntityName = (state) => {
  if (
    state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.entities
    && state.login.jwtClaim.additionalInfo.entities.length > 0
  ) {
    return state.login.jwtClaim.additionalInfo.entities[0].entityname;
  } else {
    return "Unknown";
  }
};

export const selectJwtClaimUserEntityId = (state) => {
  if (
    state.login.jwtClaim && state.login.jwtClaim.additionalInfo && state.login.jwtClaim.additionalInfo.entities
    && state.login.jwtClaim.additionalInfo.entities.length > 0
  ) {
    return state.login.jwtClaim.additionalInfo.entities[0].entityid;
  } else {
    return "Unknown";
  }
};
export default loginSlice.reducer;
